import React from 'react';
import { NextoneOrderClientContext } from '../../context/NextoneOrderClientContext';
import RecipientCopyrightTransferAmendmentFormView1 from './RecipientCopyrightTransferAmendmentFormView1';
import RecipientCopyrightTransferAmendmentFormView2 from './RecipientCopyrightTransferAmendmentFormView2';
import RecipientCopyrightTransferAmendmentFormView3 from './RecipientCopyrightTransferAmendmentFormView3';
import useCustomForm, { validateField } from '../../../common/hooks/CustomForm';
import ValidationUtility from '../../../common/utility/ValidationUtility';
import { FieldValidationErrorsType, showErrorFlush } from '../../../common/form/FrekulForm';

export default function RecipientCopyrightTransferAmendmentForm({
  document,
  formHash,
  karaokeOrder,
} : {
  document: string,
  formHash: string,
  karaokeOrder: {
    title: string,
    applicant_name: string,
  }
}) {
  type AjaxName = 'validate'|'execute';
  type ViewName = 'view1'|'view2'|'view3';
  const [ajax, setAjax] = React.useState<AjaxName|null>(null);
  const [preview, setPreview] = React.useState<string|null>(null);
  const [view, setView] = React.useState<ViewName>('view1');

  type FieldName = 'applicant_address';
  type ValuesType = { [name in FieldName]: string };
  type ErrorsType = { [name in FieldName]?: FieldValidationErrorsType };
  const [values, errors, setValues, setErrors] = useCustomForm<ValuesType, ErrorsType, { name: FieldName }>({
    defaultValue: {
      applicant_address: '',
    },
    validate(values, options, errors): ErrorsType {
      const name = options.name;
      const value = values[name];
      return { ...errors, [name]: validateField([
        ['applicant_address', 'notEmpty', '住所を入力してください。', ValidationUtility.isNotEmptyText],
      ], name, value) }
    },
  });

  const {
    validateRecipientCopyrightTransferAmendment,
    executeRecipientCopyrightTransferAmendment,
  } = React.useContext(NextoneOrderClientContext);

  React.useEffect(() => {
    if (ajax === 'validate') {
      validateRecipientCopyrightTransferAmendment(formHash, values.applicant_address).then((responseData) => {
        setAjax(null);
        setErrors(responseData.validationErrors || {});
        if (responseData.result) {
          setPreview(responseData.document);
          setView('view3');
        } else {
          showErrorFlush('入力内容に不備があります。')
        }
      });
    } else if (ajax === 'execute') {
      executeRecipientCopyrightTransferAmendment(formHash, values.applicant_address).then((responseData) => {
        setAjax(null);
        setErrors(responseData.validationErrors || {});
        if (responseData.result) {
          window.location.href = `/nextone_orders/recipient_copyright_transfer_agreement_amendment_completed/${formHash}`;
        } else {
          showErrorFlush('入力内容に不備があります。')
        }
      });
    }
  }, [ajax]);

  const topRef = React.useRef(null);
  React.useEffect(() => {
    (topRef.current as HTMLElement|null)?.scrollIntoView();
  }, [view]);

  return (
    <>
      <div ref={topRef} />
      {view === 'view1' && (
        <RecipientCopyrightTransferAmendmentFormView1
          karaokeOrder={karaokeOrder}
          onNext={() => {
            setView('view2');
          }}
        />
      )}
      {view === 'view2' && (
        <RecipientCopyrightTransferAmendmentFormView2
          document={document}
          karaokeOrder={karaokeOrder}
          getValue={(name) => values[name]}
          setValue={(name, value) => setValues({ ...values, [name]: value }, { name })}
          getErrors={(name) => errors?.[name] || {}}
          onNext={() => {
            setAjax('validate');
          }}
        />
      )}
      {view === 'view3' && (
        <RecipientCopyrightTransferAmendmentFormView3
          document={preview || ''}
          karaokeOrder={karaokeOrder}
          getValue={(name) => values[name]}
          onNext={() => {
            setAjax('execute');
          }}
          onBack={() => {
            setView('view2');
          }}
        />
      )}
    </>
  );
}

